import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { Box, Button, Paper, Stack } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { ApiError } from '../../@types';
import type {
  DatabaseActiveQuery,
  TerminateDatabaseQueriesResponse,
  TerminateDatabaseQueryResponse,
} from '../../@types/api';
import { runtimeEnv } from '../../config/runtimeEnv';
import API_ROUTES from '../../constants/apiRoutes';
import { useModal } from '../../contexts/ModalContext';
import { useToast } from '../../contexts/ToastContext';
import { useToastErrorMessage } from '../../hooks/useToastErrorMessage';
import { usePagination } from '../../hooks/usePagination';
import { createColumn } from '../../utils';
import { postData } from '../../utils/api';
import NumberInput from '../atoms/NumberInput';
import StyledDataGrid from '../organisms/StyledDataGrid';
import { ResponsiveTooltip } from '../atoms/ResponsiveTooltip';
import ConfirmationModal from '../modals/ConfirmationModal';

const styles = {
  page: {
    width: '100%',
    gap: 0.5,
  },
  content: {
    gap: 0.5,
  },
  section: {
    px: { xs: 2, md: 3 },
    pt: 0,
    pb: { xs: 1, md: 1.5 },
    borderRadius: 2,
    gap: 0.5,
  },
  terminateControls: {
    width: '100%',
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: { xs: 'stretch', md: 'center' },
    justifyContent: 'flex-end',
    gap: { xs: 1.5, md: 1.5 },
    minHeight: { xs: 'auto', md: 68 },
    py: { xs: 0, md: 0.5 },
  },
  terminateCopy: {
    minWidth: 0,
    maxWidth: { md: 520 },
    textAlign: { xs: 'left', md: 'right' },
    lineHeight: 1.4,
  },
  terminateForm: {
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'stretch', sm: 'center' },
    gap: 1,
    width: { xs: '100%', md: 'auto' },
    flexShrink: 0,
  },
  minutesInput: {
    width: { xs: '100%', sm: 72 },
  },
  queryCell: {
    display: 'block',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
  queryTooltipContent: {
    maxWidth: 720,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  terminateButton: {
    minWidth: 108,
    borderRadius: '999px',
    px: 2,
  },
  refreshButton: {
    minWidth: 98,
    borderRadius: '999px',
    px: 1.5,
  },
  monitoringButton: {
    minWidth: 178,
    borderRadius: '999px',
    px: 1.5,
  },
  table: {
    width: '100%',
  },
  rowTerminateButton: {
    minWidth: 0,
    px: 1.5,
    borderRadius: '999px',
    fontWeight: 600,
    textTransform: 'none',
    borderColor: 'rgba(211, 47, 47, 0.28)',
    '&:hover': {
      borderColor: 'error.main',
      backgroundColor: 'rgba(211, 47, 47, 0.05)',
    },
  },
};

const DEFAULT_PAGE_SIZE = 15;
const DATABASE_QUERIES_QUERY_KEY = [API_ROUTES.SUPER_ADMIN_DATABASE_ACTIVE_QUERIES] as const;

const SuperAdminDatabaseQueriesPage = () => {
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();
  const { enqueueSnackbar } = useToast();
  const { handleErrorMessage } = useToastErrorMessage();
  const [terminationMinutes, setTerminationMinutes] = useState('1');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE });

  const {
    data: databaseQueries,
    updateQueryParams,
    isFetching,
    isLoading,
  } = usePagination<DatabaseActiveQuery>(
    API_ROUTES.SUPER_ADMIN_DATABASE_ACTIVE_QUERIES,
    { page: 1, limit: DEFAULT_PAGE_SIZE },
    {
      refetchInterval: 30000,
    },
    'ftbConsumer'
  );

  const handlePaginationModelChange = (model: { page: number; pageSize: number }) => {
    const nextPaginationModel = {
      ...model,
      pageSize: DEFAULT_PAGE_SIZE,
    };

    setPaginationModel(nextPaginationModel);
    updateQueryParams(nextPaginationModel);
  };

  const invalidateDatabaseQueries = () => queryClient.invalidateQueries({ queryKey: DATABASE_QUERIES_QUERY_KEY });

  const terminateLongRunningMutation = useMutation<TerminateDatabaseQueriesResponse, ApiError, number>({
    mutationFn: async (minutes) => {
      return postData(API_ROUTES.SUPER_ADMIN_TERMINATE_DATABASE_QUERIES, { minutes }, 'ftbConsumer');
    },
    onSuccess: async (result) => {
      enqueueSnackbar({
        message: `Terminated ${result.terminatedCount} quer${result.terminatedCount === 1 ? 'y' : 'ies'}.`,
        variant: 'success',
      });
      await invalidateDatabaseQueries();
    },
    onError: (error) => {
      handleErrorMessage(error);
    },
  });

  const terminateSingleQueryMutation = useMutation<TerminateDatabaseQueryResponse, ApiError, number>({
    mutationFn: async (pid) => {
      return postData(`${API_ROUTES.SUPER_ADMIN_DATABASE_ACTIVE_QUERIES}/${pid}/terminate`, {}, 'ftbConsumer');
    },
    onSuccess: async (result) => {
      enqueueSnackbar({
        message: result.terminated
          ? `Query #${result.pid} terminated successfully.`
          : `Query #${result.pid} could not be terminated.`,
        variant: result.terminated ? 'success' : 'warning',
      });
      await invalidateDatabaseQueries();
    },
    onError: (error) => {
      handleErrorMessage(error);
    },
  });

  const getMinutesValue = () => {
    return Math.max(1, Number(terminationMinutes) || 1);
  };

  const handleRefreshDatabaseQueries = async () => {
    try {
      await invalidateDatabaseQueries();
    } catch (error) {
      handleErrorMessage(error as ApiError);
    }
  };

  const openTerminateLongRunningConfirmationModal = () => {
    const minutes = getMinutesValue();
    openModal(
      <ConfirmationModal
        title={`Terminate all active queries running longer than ${minutes} minute${minutes === 1 ? '' : 's'}?`}
        actionButtonLabel='Confirm'
        actionButtonClose='Cancel'
        handleConfirm={() => terminateLongRunningMutation.mutate(getMinutesValue())}
        closeModal={closeModal}
      />
    );
  };

  const openTerminateSingleQueryConfirmationModal = (pid: number) => {
    openModal(
      <ConfirmationModal
        title={`Terminate query #${pid}?`}
        actionButtonLabel='Confirm'
        actionButtonClose='Cancel'
        handleConfirm={() => terminateSingleQueryMutation.mutate(pid)}
        closeModal={closeModal}
      />
    );
  };

  const columns: GridColDef[] = [
    createColumn('pid', 'PID', {
      minWidth: 110,
      flex: 0.35,
      sortable: false,
    }),
    createColumn('age', 'Age', {
      minWidth: 210,
      flex: 0.7,
      sortable: false,
    }),
    createColumn('usename', 'User', {
      minWidth: 120,
      flex: 0.45,
      sortable: false,
    }),
    createColumn('state', 'State', {
      minWidth: 140,
      flex: 0.5,
      sortable: false,
    }),
    createColumn('waitEvent', 'Wait Event', {
      minWidth: 160,
      flex: 0.55,
      sortable: false,
      valueGetter: (value, row, column, apiRef) => row.waitEvent ?? 'N/A',
    }),
    createColumn('query', 'Query', {
      minWidth: 360,
      flex: 1.6,
      sortable: false,
      renderCell: (params: GridRenderCellParams<DatabaseActiveQuery>) => (
        <ResponsiveTooltip content={<Box sx={styles.queryTooltipContent}>{params.row.query}</Box>}>
          <Box sx={styles.queryCell}>{params.row.query}</Box>
        </ResponsiveTooltip>
      ),
    }),
    createColumn('actions', 'Actions', {
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<DatabaseActiveQuery>) => (
        <Button
          variant='outlined'
          color='error'
          size='small'
          aria-label={`Terminate query ${params.row.pid}`}
          startIcon={<BlockOutlinedIcon fontSize='small' />}
          sx={styles.rowTerminateButton}
          onClick={(event) => {
            event.stopPropagation();
            openTerminateSingleQueryConfirmationModal(params.row.pid);
          }}
        >
          Terminate
        </Button>
      ),
    }),
  ];

  return (
    <Stack sx={styles.page}>
      <Paper elevation={0} sx={styles.section}>
        <Stack sx={styles.content}>
          <Stack sx={styles.terminateControls}>
            <Stack sx={styles.terminateCopy}>
              <Box sx={{ typography: 'body2', color: 'text.secondary' }}>
                Terminate active queries running longer than the selected number of minutes.
              </Box>
            </Stack>

            <Stack sx={styles.terminateForm}>
              <NumberInput
                label='Minutes'
                size='small'
                value={terminationMinutes}
                valueAsString
                allowNegative={false}
                allowDecimals={false}
                onChange={(value) => setTerminationMinutes(String(value ?? ''))}
                sx={styles.minutesInput}
              />
              <Button
                variant='contained'
                color='error'
                size='small'
                onClick={openTerminateLongRunningConfirmationModal}
                disabled={terminateLongRunningMutation.isPending}
                sx={styles.terminateButton}
              >
                Terminate
              </Button>
              <Button
                variant='outlined'
                color='inherit'
                size='small'
                startIcon={<RefreshOutlinedIcon fontSize='small' />}
                onClick={handleRefreshDatabaseQueries}
                disabled={isFetching || isLoading}
                sx={styles.refreshButton}
              >
                Refresh
              </Button>
              <Button
                component='a'
                variant='outlined'
                color='primary'
                size='small'
                href={runtimeEnv.superAdminLongRunningQueriesZabbixUrl}
                target='_blank'
                rel='noopener noreferrer'
                title={
                  runtimeEnv.superAdminLongRunningQueriesZabbixUrl
                    ? undefined
                    : 'Zabbix long running queries monitoring is not configured for this environment.'
                }
                startIcon={<OpenInNewOutlinedIcon fontSize='small' />}
                disabled={!runtimeEnv.superAdminLongRunningQueriesZabbixUrl}
                sx={styles.monitoringButton}
              >
                Zabbix long running queries
              </Button>
            </Stack>
          </Stack>

          <StyledDataGrid
            rows={databaseQueries?.items ?? []}
            columns={columns}
            rowCount={databaseQueries?.count ?? 0}
            loading={isFetching || isLoading}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[DEFAULT_PAGE_SIZE]}
            getRowId={(row) => row.pid}
            sx={styles.table}
          />
        </Stack>
      </Paper>
    </Stack>
  );
};

export default SuperAdminDatabaseQueriesPage;
